// Canonical TypeScript declaration of the kolilang.wasm ABI.
//
// This file is the **contract** between the Zig core (src/wasm_main.zig +
// src/duckdb_wasm.zig) and every JS/TS consumer in web/. Treat kolilang.wasm
// as if it were a published npm package: the only legal way to use it is
// through the types and constants declared here. Anything reaching past
// these types into Zig-side assumptions is a layering violation.
//
// Sync between sides:
//   - `KolilangExports` mirrors the `export fn` set in `src/wasm_main.zig`.
//   - `HOST_IMPORT_NAMES` mirrors the `extern fn` set in `src/duckdb_wasm.zig`.
//   - `HEADER_MAGIC` / `HEADER_VERSION` / `HEADER_SIZE` mirror the
//     `writeHeader` site in `src/wasm_main.zig`.
//
// Changing any of these requires a coordinated Zig-side change. Web-only
// agents (e.g. .claude/agents/web-builder.md) must NOT edit Zig source —
// file an issue describing the ABI change and hand back to the Zig agent.

// --- Result envelope -----------------------------------------------------
//
// Every successful lex / parse / plan / run call returns a buffer with
// this layout:
//
//   bytes 0-3   : magic 'SASS' (little-endian u32)
//   bytes 4-7   : version (u32, currently 1)
//   bytes 8-11  : payload length (u32)
//   bytes 12..  : UTF-8 JSON payload
//
// `out_len` (the third arg of every entry point) is set to
// 12 + payloadLen so the host frees the full buffer.

export const HEADER_MAGIC = 0x53534153; // 'SASS' little-endian
export const HEADER_VERSION = 1;
export const HEADER_SIZE = 12;

// --- Exports -------------------------------------------------------------
//
// Full surface of the wasm module. Consumers that only need a subset
// (e.g. the run-only worker) still use this type — TypeScript structural
// typing makes the extra fields invisible to call sites.

export interface KolilangExports {
  /** WASM linear memory. Re-read `.buffer` after any call that may have
   *  grown memory; never cache a typed-array view across an export. */
  memory: WebAssembly.Memory;

  /** Allocate `n` bytes in linear memory. Returns 0 on OOM. */
  kolilang_alloc(n: number): number;
  /** Free a buffer the host previously got from `kolilang_alloc`. The size
   *  must match the original allocation. */
  kolilang_free(ptr: number, n: number): void;
  /** Free a result buffer the wasm side allocated (returned by lex /
   *  parse / plan / run). Size must equal `*out_len`. */
  kolilang_free_result(ptr: number, n: number): void;

  /** Pointer to a null-terminated UTF-8 version string. The pointer is
   *  static — no free needed. */
  kolilang_version(): number;

  kolilang_lex(src_ptr: number, src_len: number, out_len_ptr: number): number;
  kolilang_parse(src_ptr: number, src_len: number, out_len_ptr: number): number;
  kolilang_plan(src_ptr: number, src_len: number, out_len_ptr: number): number;
  /** Free-only shortcut; equivalent to `kolilang_run_with_ctx(..., 0, 0, ...)`.
   *  Paid procs (REPORT, COMPARE, TABULATE, statistical procs) will emit
   *  the license-required ERROR. */
  kolilang_run(src_ptr: number, src_len: number, out_len_ptr: number): number;
  /** Full evaluator with an explicit license context. `ctx_ptr` /
   *  `ctx_len` carry a UTF-8 JSON document of the shape declared by
   *  `LicenseContextWire` below. `ctx_len === 0` (or `ctx_ptr === 0`)
   *  is treated as free_only. Malformed JSON / unknown kind surface
   *  as a stderr line in the result envelope; the run still proceeds
   *  under free_only so a bad JWT can never crash the page. */
  kolilang_run_with_ctx(
    src_ptr: number,
    src_len: number,
    ctx_ptr: number,
    ctx_len: number,
    out_len_ptr: number,
  ): number;
}

// --- LicenseContext wire format ------------------------------------------
//
// Mirrors `src/license_ctx.zig`'s `LicenseContext` struct. The web auth
// layer (B4) derives this from the JWT's `features[]` claim and feeds
// it to `kolilang_run_with_ctx`. The Zig side parses it via
// `license_ctx.parseFromJson` — only `kind` drives entitlement;
// `license_email` and `trial_days_left` are surfaced in error messages
// and telemetry but don't gate anything.

export type LicenseKind = 'free_only' | 'trial' | 'licensed';

export interface LicenseContextWire {
  kind: LicenseKind;
  /** Email on a `.licensed` entitlement. Omit (or null) on free/trial. */
  license_email?: string | null;
  /** Days remaining on a `.trial` entitlement. Omit otherwise. */
  trial_days_left?: number | null;
}

/// Serialize a `LicenseContextWire` to the exact JSON shape the WASM
/// parser accepts. Skips null/undefined fields so they don't trip
/// the wrong-type-on-optional-field branch in the parser.
export function encodeLicenseContext(ctx: LicenseContextWire): string {
  const parts: string[] = [`"kind":${JSON.stringify(ctx.kind)}`];
  if (ctx.license_email != null) {
    parts.push(`"license_email":${JSON.stringify(ctx.license_email)}`);
  }
  if (ctx.trial_days_left != null) {
    parts.push(`"trial_days_left":${ctx.trial_days_left}`);
  }
  return `{${parts.join(',')}}`;
}

// --- Host imports --------------------------------------------------------
//
// The wasm module imports these from its `env` namespace. They're the
// DuckDB-wasm bridge surface — only `kolilang_run` actually calls them, so
// stubs that throw are sufficient for lex/parse/plan-only consumers
// (Node test, main thread that delegates run to a worker).
//
// MUST stay in sync with the `extern fn` declarations in
// `src/duckdb_wasm.zig`. Failure to stub a new import surfaces as a
// `LinkError: import not found` at `WebAssembly.instantiate` time.

export const HOST_IMPORT_NAMES = [
  'host_run_sql',
  'host_result_destroy',
  'host_result_error_len',
  'host_result_error_copy',
  'host_result_row_count',
  'host_result_column_count',
  'host_result_column_type',
  'host_result_column_name_copy',
  'host_result_cell_double',
  'host_result_cell_int64',
  'host_result_cell_is_null',
  'host_result_cell_text_len',
  'host_result_cell_text_copy',
] as const;

export type HostImportName = (typeof HOST_IMPORT_NAMES)[number];
export type HostImports = Record<string, (...args: never[]) => unknown>;

/// Build a `HostImports` object whose every required entry throws with a
/// descriptive message. Suitable for lex/parse/plan callers that never
/// invoke `kolilang_run`, and for tests that want to catch accidental calls
/// rather than silently no-op'ing.
export function throwingHostImports(label: string = 'kolilang'): HostImports {
  const out: HostImports = {};
  for (const name of HOST_IMPORT_NAMES) {
    out[name] = () => {
      throw new Error(`${label}: ${name} called but not wired`);
    };
  }
  return out;
}
